import React, { useState } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Container,
  Grid,
  Button,
  TextField,
  Modal,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import VideoUpload from "./VideoUpload";
import MetadataDisplay from "./MetadataDisplay";
import axios from "axios";
import { format, setHours, setMinutes } from "date-fns";
import "../App.css";

const steps = [
  "Upload Video and View Metadata",
  "Get Timestamp and Location",
  "Enter Contact Email",
  "Confirm and Analyze",
];

const AnalysisPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [metadata, setMetadata] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [address, setAddress] = useState({
    street: "",
    city: "",
    state: "",
    zip: "",
  });
  const [coordinates, setCoordinates] = useState(null);
  const [requestorEmail, setRequestorEmail] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
  const [coordinateError, setCoordinateError] = useState(null);

  const resetSteps = () => {
    setActiveStep(0);
    setMetadata(null);
    setDate(null);
    setTime(null);
    setAddress({
      street: "",
      city: "",
      state: "",
      zip: "",
    });
    setCoordinates(null);
    setRequestorEmail("");
    setVideoFile(null);
    setOpenModal(false);
    setCoordinateError(null);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const combineDateAndTime = () => {
    if (date && time) {
      const combinedDate = setHours(
        setMinutes(date, time.getMinutes()),
        time.getHours()
      );
      return format(combinedDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
    }
    return null;
  };

  const handleGetCoordinates = async () => {
    setIsLoadingCoordinates(true);
    setCoordinateError(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/jobs/get-coordinates`,
        {
          street: address.street,
          city: address.city,
          state: address.state,
          zip: address.zip,
        }
      );

      if (response.status === 200) {
        setCoordinates({
          lat: response.data.latitude,
          long: response.data.longitude,
        });
      } else {
        setCoordinateError("Error fetching coordinates. Please try again.");
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setCoordinateError("Rate limit exceeded. Please wait a moment and try again.");
      } else {
        setCoordinateError(
          error.response?.data?.error ||
            "Error fetching coordinates. Please try again."
        );
      }
    } finally {
      setIsLoadingCoordinates(false);
    }
  };

  const handleAnalyze = async () => {
    const timestamp = combineDateAndTime();
    const resourceMetaData = {
      ...metadata,
      timeOfDay: timestamp,
      gpsCoordinates: coordinates
        ? `${coordinates.lat},${coordinates.long}`
        : null,
      address: {
        street: address.street,
        city: address.city,
        state: address.state,
        zip: address.zip,
      },
    };

    const formData = new FormData();
    formData.append("category", "Truck Detection");
    formData.append("resource", videoFile);
    formData.append("resourceMetaData", JSON.stringify(resourceMetaData));
    formData.append("requestorEmail", requestorEmail);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/jobs/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        setModalMessage("Job has been created. Check back later for analysis.");
      } else {
        setModalMessage("Error Starting Job, Please try again.");
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setModalMessage("Rate limit exceeded. Please wait a moment and try again.");
      } else {
        setModalMessage("Error Starting Job, Please try again.");
      }
    } finally {
      setOpenModal(true);
    }
  };

  const handleClose = () => {
    if (
      modalMessage === "Job has been created. Check back later for analysis."
    ) {
      resetSteps();
    } else {
      setOpenModal(false);
    }
  };

  const isStep1Complete = metadata !== null;
  const isStep2Complete = date && time && coordinates;

  const ConfirmationCard = () => (
    <Card variant="outlined" sx={{ mt: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Confirmation Details
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Video File"
              secondary={videoFile ? videoFile.name : "No file uploaded"}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Date and Time"
              secondary={combineDateAndTime() || "Not set"}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Address"
              secondary={`${address.street}, ${address.city}, ${address.state} ${address.zip}`}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Coordinates"
              secondary={
                coordinates
                  ? `Lat: ${coordinates.lat}, Long: ${coordinates.long}`
                  : "Not set"
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Contact Email"
              secondary={requestorEmail || "Not provided"}
            />
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container className="App" maxWidth="md">
        <Box sx={{ width: "100%", mt: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label} completed={activeStep > index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Box mt={4}>
          {activeStep === 0 && (
            <>
              <Typography variant="h5" component="h2" gutterBottom>
                Step 1: Upload Video and View Metadata
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <VideoUpload
                    setMetadata={setMetadata}
                    setVideoFile={setVideoFile}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MetadataDisplay metadata={metadata} />
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={!isStep1Complete}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Typography variant="h5" component="h2" gutterBottom>
                Step 2: Get Timestamp and Location
              </Typography>
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      label="Date"
                      value={date}
                      onChange={(newValue) => setDate(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TimePicker
                      label="Time"
                      value={time}
                      onChange={(newValue) => setTime(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="Street Address"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={address.street}
                  onChange={(e) =>
                    setAddress({ ...address, street: e.target.value })
                  }
                />
                <TextField
                  label="City"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={address.city}
                  onChange={(e) =>
                    setAddress({ ...address, city: e.target.value })
                  }
                />
                <TextField
                  label="State"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={address.state}
                  onChange={(e) =>
                    setAddress({ ...address, state: e.target.value })
                  }
                />
                <TextField
                  label="Zip Code"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={address.zip}
                  onChange={(e) =>
                    setAddress({ ...address, zip: e.target.value })
                  }
                />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={2}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGetCoordinates}
                    disabled={isLoadingCoordinates}
                  >
                    {isLoadingCoordinates ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Get Coordinates"
                    )}
                  </Button>
                  {coordinates && (
                    <Typography>
                      Lat: {coordinates.lat}, Long: {coordinates.long}
                    </Typography>
                  )}
                </Box>
                {coordinateError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {coordinateError}
                  </Alert>
                )}
              </Box>
              <Box mt={2} display="flex" justifyContent="space-between">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={!isStep2Complete}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
          {activeStep === 2 && (
            <>
              <Typography variant="h5" component="h2" gutterBottom>
                Step 3: Enter Contact Email
              </Typography>
              <Box mt={2}>
                <TextField
                  label="Contact Email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={requestorEmail}
                  onChange={(e) => setRequestorEmail(e.target.value)}
                />
              </Box>
              <Box mt={2} display="flex" justifyContent="space-between">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
          {activeStep === 3 && (
            <>
              <Typography variant="h5" component="h2" gutterBottom>
                Step 4: Confirm and Analyze
              </Typography>
              <ConfirmationCard />
              <Box mt={2} display="flex" justifyContent="space-between">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAnalyze}
                >
                  Begin Analyzing Video
                </Button>
              </Box>
            </>
          )}
        </Box>
        <Modal
          open={openModal}
          onClose={handleClose}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
        >
          <Box className="modal">
            <Typography variant="h6" id="simple-modal-title">
              {modalMessage}
            </Typography>
            <Button variant="contained" color="primary" onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Modal>
      </Container>
    </LocalizationProvider>
  );
};

export default AnalysisPage;
